export const DEMO_LAST_UPDATED = '2026-04-07T13:30:00Z';
export const DEFAULT_TICKER = 'AAPL';
export const DEMO_NOTICE =
  'Demo data only. Nothing here is live market data or trading advice.';

const makeFeedItem = ({
  id,
  ticker,
  source,
  platform,
  publishedAt,
  title,
  content,
  sentimentLabel,
  sentimentScore,
  author,
  metrics,
}) => ({
  id,
  ticker,
  source,
  platform,
  publishedAt,
  title,
  content,
  sentiment: {
    label: sentimentLabel,
    score: sentimentScore,
  },
  author,
  metrics,
  synthetic: true,
});

const TICKER_DATA = {
  AAPL: {
    symbol: 'AAPL',
    company: 'Apple',
    sector: 'Consumer Technology',
    price: 214.36,
    dayChange: 1.8,
    summary:
      'Demand signals point to a steadier device refresh cycle and durable services revenue mix.',
    demoHeadline: 'Services chatter and upgrade optimism keep the tone constructive.',
    themes: ['services mix', 'device refresh', 'capital returns'],
    metrics: {
      conviction: 82,
      buzzChange: 14,
      sourceDiversity: 77,
      riskNote: 'One delayed keynote and the whole room starts acting like it saw a ghost.',
    },
    sentimentSummary: {
      score: 0.68,
      delta: 0.07,
      confidence: 0.79,
      totalPosts: 348,
      positive: 184,
      neutral: 103,
      negative: 61,
    },
    sourceBreakdown: {
      news: { count: 118, sentiment: { positive: 63, neutral: 34, negative: 21 } },
      reddit: { count: 104, sentiment: { positive: 57, neutral: 31, negative: 16 } },
      stocktwits: { count: 126, sentiment: { positive: 64, neutral: 38, negative: 24 } },
    },
    timeline: [
      { timestamp: '2026-04-01T20:00:00Z', overallScore: 0.55, confidence: 0.65, totalPosts: 121, positive: 54, neutral: 39, negative: 28, price: 206.8 },
      { timestamp: '2026-04-02T20:00:00Z', overallScore: 0.58, confidence: 0.67, totalPosts: 129, positive: 60, neutral: 40, negative: 29, price: 208.1 },
      { timestamp: '2026-04-03T20:00:00Z', overallScore: 0.61, confidence: 0.7, totalPosts: 138, positive: 67, neutral: 43, negative: 28, price: 209.5 },
      { timestamp: '2026-04-04T20:00:00Z', overallScore: 0.63, confidence: 0.73, totalPosts: 145, positive: 74, neutral: 43, negative: 28, price: 210.9 },
      { timestamp: '2026-04-05T20:00:00Z', overallScore: 0.66, confidence: 0.75, totalPosts: 153, positive: 80, neutral: 45, negative: 28, price: 212.4 },
      { timestamp: '2026-04-06T20:00:00Z', overallScore: 0.67, confidence: 0.77, totalPosts: 166, positive: 86, neutral: 49, negative: 31, price: 213.3 },
      { timestamp: '2026-04-07T20:00:00Z', overallScore: 0.68, confidence: 0.79, totalPosts: 174, positive: 92, neutral: 51, negative: 31, price: 214.36 },
    ],
    feed: [
      makeFeedItem({
        id: 'aapl-news-1',
        ticker: 'AAPL',
        source: 'news',
        platform: 'Signal Ledger',
        publishedAt: '2026-04-07T12:10:00Z',
        title: 'Orchard One bundle rumor has half the Street acting like it found religion',
        content:
          'A whisper about an Orchard One bundle sent analysts into their favorite hobby: building twelve-tab spreadsheets and calling it intuition.',
        sentimentLabel: 'positive',
        sentimentScore: 0.78,
        author: 'Mira Santos',
        metrics: { reactions: 184, comments: 22 },
      }),
      makeFeedItem({
        id: 'aapl-news-2',
        ticker: 'AAPL',
        source: 'news',
        platform: 'Market Mosaic',
        publishedAt: '2026-04-06T15:45:00Z',
        title: 'Supplier note says the parts bin may finally stop behaving like a haunted pantry',
        content:
          'Supply chains look calmer, although everyone still lowers their voice whenever launch timing comes up.',
        sentimentLabel: 'positive',
        sentimentScore: 0.66,
        author: 'Jonah Ellis',
        metrics: { reactions: 133, comments: 16 },
      }),
      makeFeedItem({
        id: 'aapl-news-3',
        ticker: 'AAPL',
        source: 'news',
        platform: 'Paper Street Wire',
        publishedAt: '2026-04-05T17:20:00Z',
        title: 'Analysts split on premium hardware demand after a suspiciously calm quarter',
        content:
          'Channel checks showed balanced demand, with bulls leaning on loyalty and bears focusing on a softer upgrade pace in lower-cost models.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.52,
        author: 'Rhea Brooks',
        metrics: { reactions: 96, comments: 11 },
      }),
      makeFeedItem({
        id: 'aapl-reddit-1',
        ticker: 'AAPL',
        source: 'reddit',
        platform: 'r/marketlab',
        publishedAt: '2026-04-07T11:00:00Z',
        title: 'Services margin keeps this setup cleaner than it has any right to be',
        content:
          'Recurring revenue is doing the heavy lifting here. Every dip looks like it gets bought by someone with three monitors and too much conviction.',
        sentimentLabel: 'positive',
        sentimentScore: 0.74,
        author: 'u/charting_circuits',
        metrics: { reactions: 422, comments: 68 },
      }),
      makeFeedItem({
        id: 'aapl-reddit-2',
        ticker: 'AAPL',
        source: 'reddit',
        platform: 'r/valuetech',
        publishedAt: '2026-04-06T18:35:00Z',
        title: 'Anyone else think the replacement cycle is already priced in?',
        content:
          'Momentum looks good, but unless accessories suddenly become a personality trait, I do not see a giant upside surprise.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.49,
        author: 'u/sideways_signal',
        metrics: { reactions: 287, comments: 51 },
      }),
      makeFeedItem({
        id: 'aapl-reddit-3',
        ticker: 'AAPL',
        source: 'reddit',
        platform: 'r/earningsfloor',
        publishedAt: '2026-04-05T13:10:00Z',
        title: 'Bear case still hangs on slower premium handset volumes',
        content:
          'The setup is solid, but if upgrades slow down this goes from beloved to “let us revisit next quarter” in a hurry.',
        sentimentLabel: 'negative',
        sentimentScore: 0.35,
        author: 'u/macro_mockup',
        metrics: { reactions: 198, comments: 39 },
      }),
      makeFeedItem({
        id: 'aapl-stocktwits-1',
        ticker: 'AAPL',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-07T12:55:00Z',
        title: '$AAPL tape keeps grinding like it has rent due',
        content:
          'Buyers keep defending 212 like it is union work. Tone stays constructive while services headlines keep printing.',
        sentimentLabel: 'positive',
        sentimentScore: 0.76,
        author: '@pixelalpha',
        metrics: { reactions: 316, comments: 27 },
      }),
      makeFeedItem({
        id: 'aapl-stocktwits-2',
        ticker: 'AAPL',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-06T14:40:00Z',
        title: 'Mock momentum intact, but volume is not explosive',
        content:
          'Still a decent setup. I just want a stronger burst in participation before calling this a clean breakout and embarrassing myself publicly.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.54,
        author: '@terminalcitrus',
        metrics: { reactions: 201, comments: 19 },
      }),
      makeFeedItem({
        id: 'aapl-stocktwits-3',
        ticker: 'AAPL',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-05T09:30:00Z',
        title: 'Bulls win unless launch timing slips again',
        content:
          'Chart stays friendly, but one ugly delay rumor and the mood changes faster than airport weather.',
        sentimentLabel: 'negative',
        sentimentScore: 0.38,
        author: '@riskdraft',
        metrics: { reactions: 144, comments: 12 },
      }),
    ],
  },
  TSLA: {
    symbol: 'TSLA',
    company: 'Tesla',
    sector: 'Electric Vehicles',
    price: 191.42,
    dayChange: -2.1,
    summary:
      'Conversation is energetic but divided, with product hype offset by margin and pricing pressure concerns.',
    demoHeadline: 'High buzz, mixed conviction, and a wider spread between bullish and bearish scenarios.',
    themes: ['robotaxi storyline', 'pricing pressure', 'manufacturing scale'],
    metrics: {
      conviction: 61,
      buzzChange: 22,
      sourceDiversity: 71,
      riskNote: 'Every time someone says “price cut,” three bulls immediately pretend they lost service.',
    },
    sentimentSummary: {
      score: 0.47,
      delta: -0.04,
      confidence: 0.68,
      totalPosts: 412,
      positive: 156,
      neutral: 118,
      negative: 138,
    },
    sourceBreakdown: {
      news: { count: 122, sentiment: { positive: 40, neutral: 41, negative: 41 } },
      reddit: { count: 146, sentiment: { positive: 59, neutral: 33, negative: 54 } },
      stocktwits: { count: 144, sentiment: { positive: 57, neutral: 44, negative: 43 } },
    },
    timeline: [
      { timestamp: '2026-04-01T20:00:00Z', overallScore: 0.54, confidence: 0.7, totalPosts: 151, positive: 68, neutral: 39, negative: 44, price: 198.7 },
      { timestamp: '2026-04-02T20:00:00Z', overallScore: 0.52, confidence: 0.7, totalPosts: 158, positive: 70, neutral: 41, negative: 47, price: 197.4 },
      { timestamp: '2026-04-03T20:00:00Z', overallScore: 0.5, confidence: 0.69, totalPosts: 166, positive: 71, neutral: 46, negative: 49, price: 195.9 },
      { timestamp: '2026-04-04T20:00:00Z', overallScore: 0.49, confidence: 0.69, totalPosts: 171, positive: 70, neutral: 49, negative: 52, price: 194.2 },
      { timestamp: '2026-04-05T20:00:00Z', overallScore: 0.48, confidence: 0.68, totalPosts: 176, positive: 68, neutral: 52, negative: 56, price: 193.8 },
      { timestamp: '2026-04-06T20:00:00Z', overallScore: 0.47, confidence: 0.68, totalPosts: 181, positive: 66, neutral: 54, negative: 61, price: 192.1 },
      { timestamp: '2026-04-07T20:00:00Z', overallScore: 0.47, confidence: 0.68, totalPosts: 188, positive: 67, neutral: 56, negative: 65, price: 191.42 },
    ],
    feed: [
      makeFeedItem({
        id: 'tsla-news-1',
        ticker: 'TSLA',
        source: 'news',
        platform: 'Signal Ledger',
        publishedAt: '2026-04-07T11:35:00Z',
        title: 'Robotaxi teaser boosts engagement but still cannot pay the margin bills',
        content:
          'The teaser lit up the room, but nobody could explain who exactly is paying for all this enthusiasm.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.51,
        author: 'Ada Moreno',
        metrics: { reactions: 208, comments: 31 },
      }),
      makeFeedItem({
        id: 'tsla-news-2',
        ticker: 'TSLA',
        source: 'news',
        platform: 'Market Mosaic',
        publishedAt: '2026-04-06T16:20:00Z',
        title: 'Channel checks suggest discounting is still the mood-killer at the party',
        content:
          'A pricing note cooled the mood right after a run of autonomy headlines had everyone posting like they owned the moon.',
        sentimentLabel: 'negative',
        sentimentScore: 0.34,
        author: 'Nina Patel',
        metrics: { reactions: 171, comments: 24 },
      }),
      makeFeedItem({
        id: 'tsla-news-3',
        ticker: 'TSLA',
        source: 'news',
        platform: 'Paper Street Wire',
        publishedAt: '2026-04-05T14:45:00Z',
        title: 'Analysts map a comically wide range of outcomes for the next delivery cycle',
        content:
          'The setup still reads as high drama: plenty of upside if the story works, plenty of yelling if it does not.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.47,
        author: 'Maya Cole',
        metrics: { reactions: 149, comments: 18 },
      }),
      makeFeedItem({
        id: 'tsla-reddit-1',
        ticker: 'TSLA',
        source: 'reddit',
        platform: 'r/growthgarage',
        publishedAt: '2026-04-07T10:15:00Z',
        title: 'This still has the loudest upside if autonomy actually lands',
        content:
          'The optionality in this story is why the ticker keeps sitting near the top of every list, even when half the room is rolling its eyes.',
        sentimentLabel: 'positive',
        sentimentScore: 0.72,
        author: 'u/overclockedthesis',
        metrics: { reactions: 514, comments: 104 },
      }),
      makeFeedItem({
        id: 'tsla-reddit-2',
        ticker: 'TSLA',
        source: 'reddit',
        platform: 'r/macrodrive',
        publishedAt: '2026-04-06T20:05:00Z',
        title: 'Bear view: all the buzz, none of the pricing power',
        content:
          'The story is exciting, but every price cut takes a little nibble out of the premium multiple fantasy.',
        sentimentLabel: 'negative',
        sentimentScore: 0.29,
        author: 'u/batteryfade',
        metrics: { reactions: 403, comments: 87 },
      }),
      makeFeedItem({
        id: 'tsla-reddit-3',
        ticker: 'TSLA',
        source: 'reddit',
        platform: 'r/optionsbay',
        publishedAt: '2026-04-05T18:30:00Z',
        title: 'Wild gamma energy, but I would still size small',
        content:
          'I like the buzz, though this still behaves like a high-drama name where conviction can flip on one badly timed headline.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.46,
        author: 'u/rangeboundpilot',
        metrics: { reactions: 322, comments: 58 },
      }),
      makeFeedItem({
        id: 'tsla-stocktwits-1',
        ticker: 'TSLA',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-07T12:40:00Z',
        title: '$TSLA squeeze talk is back and somehow louder than before',
        content:
          'The tape could rip if momentum comes back, but the crowd is still arguing over whether the move would be genius or deeply annoying.',
        sentimentLabel: 'positive',
        sentimentScore: 0.69,
        author: '@gammaforge',
        metrics: { reactions: 387, comments: 43 },
      }),
      makeFeedItem({
        id: 'tsla-stocktwits-2',
        ticker: 'TSLA',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-06T13:55:00Z',
        title: 'Mock tape looks heavy every time discounting comes up',
        content:
          'This name still trades like sentiment is one headline away from rolling over. I am staying careful.',
        sentimentLabel: 'negative',
        sentimentScore: 0.33,
        author: '@brakecheck',
        metrics: { reactions: 242, comments: 21 },
      }),
      makeFeedItem({
        id: 'tsla-stocktwits-3',
        ticker: 'TSLA',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-05T08:50:00Z',
        title: 'Breakout level still contested',
        content:
          'The move can work either way from here. Volume is loud, conviction is mixed, and the range is still doing most of the talking.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.48,
        author: '@voltagelane',
        metrics: { reactions: 218, comments: 17 },
      }),
    ],
  },
  NVDA: {
    symbol: 'NVDA',
    company: 'NVIDIA',
    sector: 'Semiconductors',
    price: 978.14,
    dayChange: 3.4,
    summary:
      'Enthusiasm remains strongest here, with platform demand and AI infrastructure themes dominating the narrative.',
    demoHeadline: 'The leader right now: broad agreement, heavy participation, and almost suspiciously happy bulls.',
    themes: ['AI infrastructure', 'accelerator demand', 'software moat'],
    metrics: {
      conviction: 91,
      buzzChange: 27,
      sourceDiversity: 83,
      riskNote: 'At this altitude, even good news gets asked to show more paperwork.',
    },
    sentimentSummary: {
      score: 0.81,
      delta: 0.11,
      confidence: 0.88,
      totalPosts: 463,
      positive: 292,
      neutral: 112,
      negative: 59,
    },
    sourceBreakdown: {
      news: { count: 154, sentiment: { positive: 105, neutral: 31, negative: 18 } },
      reddit: { count: 143, sentiment: { positive: 89, neutral: 37, negative: 17 } },
      stocktwits: { count: 166, sentiment: { positive: 98, neutral: 44, negative: 24 } },
    },
    timeline: [
      { timestamp: '2026-04-01T20:00:00Z', overallScore: 0.7, confidence: 0.8, totalPosts: 166, positive: 94, neutral: 45, negative: 27, price: 924.2 },
      { timestamp: '2026-04-02T20:00:00Z', overallScore: 0.72, confidence: 0.82, totalPosts: 171, positive: 98, neutral: 46, negative: 27, price: 936.7 },
      { timestamp: '2026-04-03T20:00:00Z', overallScore: 0.74, confidence: 0.83, totalPosts: 178, positive: 104, neutral: 47, negative: 27, price: 948.8 },
      { timestamp: '2026-04-04T20:00:00Z', overallScore: 0.76, confidence: 0.84, totalPosts: 186, positive: 109, neutral: 50, negative: 27, price: 956.9 },
      { timestamp: '2026-04-05T20:00:00Z', overallScore: 0.78, confidence: 0.85, totalPosts: 192, positive: 116, neutral: 50, negative: 26, price: 964.4 },
      { timestamp: '2026-04-06T20:00:00Z', overallScore: 0.8, confidence: 0.87, totalPosts: 201, positive: 122, neutral: 52, negative: 27, price: 971.6 },
      { timestamp: '2026-04-07T20:00:00Z', overallScore: 0.81, confidence: 0.88, totalPosts: 209, positive: 129, neutral: 53, negative: 27, price: 978.14 },
    ],
    feed: [
      makeFeedItem({
        id: 'nvda-news-1',
        ticker: 'NVDA',
        source: 'news',
        platform: 'Signal Ledger',
        publishedAt: '2026-04-07T12:25:00Z',
        title: 'Data-center checks keep demand looking steeper than a ski slope in January',
        content:
          'Another strong quarter of accelerator demand has the bulls walking around like they personally soldered the chips.',
        sentimentLabel: 'positive',
        sentimentScore: 0.88,
        author: 'Leah Porter',
        metrics: { reactions: 247, comments: 29 },
      }),
      makeFeedItem({
        id: 'nvda-news-2',
        ticker: 'NVDA',
        source: 'news',
        platform: 'Market Mosaic',
        publishedAt: '2026-04-06T17:10:00Z',
        title: 'Enterprise buyers still prioritize GPU capacity like it is oxygen',
        content:
          'A CIO budget survey suggested very little cooling in infrastructure appetite, even if nobody wants to talk about valuation for too long.',
        sentimentLabel: 'positive',
        sentimentScore: 0.81,
        author: 'Drew Barnes',
        metrics: { reactions: 214, comments: 21 },
      }),
      makeFeedItem({
        id: 'nvda-news-3',
        ticker: 'NVDA',
        source: 'news',
        platform: 'Paper Street Wire',
        publishedAt: '2026-04-05T13:55:00Z',
        title: 'Strategists warn the premium now costs premium-plus',
        content:
          'Even bullish analysts flagged the risk of valuation air pockets if growth decides to take a single weekend off.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.57,
        author: 'Kara Moss',
        metrics: { reactions: 161, comments: 14 },
      }),
      makeFeedItem({
        id: 'nvda-reddit-1',
        ticker: 'NVDA',
        source: 'reddit',
        platform: 'r/aicapex',
        publishedAt: '2026-04-07T09:50:00Z',
        title: 'The moat is still bigger than the multiple, and yes I know how that sounds',
        content:
          'This name keeps printing the cleanest setup on the board. If I had to pitch one winner, it would still be NVDA and I would not overthink it.',
        sentimentLabel: 'positive',
        sentimentScore: 0.91,
        author: 'u/clustercompute',
        metrics: { reactions: 601, comments: 117 },
      }),
      makeFeedItem({
        id: 'nvda-reddit-2',
        ticker: 'NVDA',
        source: 'reddit',
        platform: 'r/semiconductorsalon',
        publishedAt: '2026-04-06T20:50:00Z',
        title: 'Real concern: when does everyone owning the same winner start to matter?',
        content:
          'The signal is still positive, but crowded trades can reverse fast when expectations get this tidy.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.58,
        author: 'u/hotaisle',
        metrics: { reactions: 338, comments: 72 },
      }),
      makeFeedItem({
        id: 'nvda-reddit-3',
        ticker: 'NVDA',
        source: 'reddit',
        platform: 'r/growthgarage',
        publishedAt: '2026-04-05T16:15:00Z',
        title: 'Still waiting for the pullback that never arrives',
        content:
          'Every dip keeps getting absorbed. That is a strength story until proven otherwise and, annoyingly, it has not been proven otherwise yet.',
        sentimentLabel: 'positive',
        sentimentScore: 0.79,
        author: 'u/fabriclatency',
        metrics: { reactions: 442, comments: 65 },
      }),
      makeFeedItem({
        id: 'nvda-stocktwits-1',
        ticker: 'NVDA',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-07T13:05:00Z',
        title: '$NVDA crowd still chasing strength with zero shame',
        content:
          'Momentum remains elite. This is still the cleanest bullish tape around, even after the latest vertical move.',
        sentimentLabel: 'positive',
        sentimentScore: 0.87,
        author: '@stackedalpha',
        metrics: { reactions: 421, comments: 39 },
      }),
      makeFeedItem({
        id: 'nvda-stocktwits-2',
        ticker: 'NVDA',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-06T14:15:00Z',
        title: 'Mock momentum is strong, but I want a cooler entry',
        content:
          'Still bullish, just not eager to chase every vertical candle while valuation is doing backflips.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.6,
        author: '@heatshield',
        metrics: { reactions: 267, comments: 23 },
      }),
      makeFeedItem({
        id: 'nvda-stocktwits-3',
        ticker: 'NVDA',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-05T10:05:00Z',
        title: 'Bears still cannot keep this tape down',
        content:
          'Every negative note keeps fading within hours. That is not the profile of a weak name.',
        sentimentLabel: 'positive',
        sentimentScore: 0.83,
        author: '@greenrack',
        metrics: { reactions: 298, comments: 20 },
      }),
    ],
  },
  MSFT: {
    symbol: 'MSFT',
    company: 'Microsoft',
    sector: 'Enterprise Software',
    price: 447.88,
    dayChange: 1.2,
    summary:
      'Tone is calm and constructive, driven by enterprise AI demand and durable cloud spending assumptions.',
    demoHeadline: 'Balanced, high-quality sentiment with less drama and more consistency.',
    themes: ['enterprise AI', 'cloud resilience', 'platform breadth'],
    metrics: {
      conviction: 86,
      buzzChange: 9,
      sourceDiversity: 81,
      riskNote: 'If this story gets any calmer, someone is going to try to call it a bond.',
    },
    sentimentSummary: {
      score: 0.73,
      delta: 0.05,
      confidence: 0.84,
      totalPosts: 321,
      positive: 201,
      neutral: 84,
      negative: 36,
    },
    sourceBreakdown: {
      news: { count: 111, sentiment: { positive: 72, neutral: 28, negative: 11 } },
      reddit: { count: 99, sentiment: { positive: 59, neutral: 28, negative: 12 } },
      stocktwits: { count: 111, sentiment: { positive: 70, neutral: 28, negative: 13 } },
    },
    timeline: [
      { timestamp: '2026-04-01T20:00:00Z', overallScore: 0.64, confidence: 0.77, totalPosts: 112, positive: 61, neutral: 33, negative: 18, price: 437.2 },
      { timestamp: '2026-04-02T20:00:00Z', overallScore: 0.66, confidence: 0.78, totalPosts: 118, positive: 64, neutral: 35, negative: 19, price: 439.4 },
      { timestamp: '2026-04-03T20:00:00Z', overallScore: 0.68, confidence: 0.8, totalPosts: 121, positive: 67, neutral: 36, negative: 18, price: 441.1 },
      { timestamp: '2026-04-04T20:00:00Z', overallScore: 0.7, confidence: 0.81, totalPosts: 127, positive: 71, neutral: 38, negative: 18, price: 443.2 },
      { timestamp: '2026-04-05T20:00:00Z', overallScore: 0.71, confidence: 0.82, totalPosts: 131, positive: 74, neutral: 39, negative: 18, price: 444.6 },
      { timestamp: '2026-04-06T20:00:00Z', overallScore: 0.72, confidence: 0.83, totalPosts: 136, positive: 77, neutral: 40, negative: 19, price: 446.5 },
      { timestamp: '2026-04-07T20:00:00Z', overallScore: 0.73, confidence: 0.84, totalPosts: 142, positive: 81, neutral: 42, negative: 19, price: 447.88 },
    ],
    feed: [
      makeFeedItem({
        id: 'msft-news-1',
        ticker: 'MSFT',
        source: 'news',
        platform: 'Signal Ledger',
        publishedAt: '2026-04-07T10:40:00Z',
        title: 'CIO survey says cloud and AI bundles remain the office-approved impulse buy',
        content:
          'Enterprise checks still point to durable spending, with buyers preferring one large vendor over six smaller headaches.',
        sentimentLabel: 'positive',
        sentimentScore: 0.79,
        author: 'Ben Arora',
        metrics: { reactions: 176, comments: 17 },
      }),
      makeFeedItem({
        id: 'msft-news-2',
        ticker: 'MSFT',
        source: 'news',
        platform: 'Market Mosaic',
        publishedAt: '2026-04-06T15:05:00Z',
        title: 'Strategists see a smoother setup than most mega-cap peers',
        content:
          'A desk note highlighted platform breadth as a stabilizer for both sentiment and revenue expectations.',
        sentimentLabel: 'positive',
        sentimentScore: 0.74,
        author: 'Noah Chen',
        metrics: { reactions: 141, comments: 11 },
      }),
      makeFeedItem({
        id: 'msft-news-3',
        ticker: 'MSFT',
        source: 'news',
        platform: 'Paper Street Wire',
        publishedAt: '2026-04-05T12:25:00Z',
        title: 'Software budgets stay firm, but seat growth cools slightly',
        content:
          'The report remained constructive overall while noting some moderation in net-new seat assumptions.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.56,
        author: 'June Walker',
        metrics: { reactions: 113, comments: 9 },
      }),
      makeFeedItem({
        id: 'msft-reddit-1',
        ticker: 'MSFT',
        source: 'reddit',
        platform: 'r/enterprisestack',
        publishedAt: '2026-04-07T09:10:00Z',
        title: 'This chart is boring in the best possible way',
        content:
          'Compared with the noisier names on the board, Microsoft reads like steady compounding with fewer headline jump scares.',
        sentimentLabel: 'positive',
        sentimentScore: 0.82,
        author: 'u/serverroombeta',
        metrics: { reactions: 294, comments: 43 },
      }),
      makeFeedItem({
        id: 'msft-reddit-2',
        ticker: 'MSFT',
        source: 'reddit',
        platform: 'r/cloudfloor',
        publishedAt: '2026-04-06T21:00:00Z',
        title: 'Upside is real, but valuation is not exactly giving anything away',
        content:
          'I like the quality score here, though I would not call it a hidden bargain unless the definition of bargain changed overnight.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.58,
        author: 'u/azureaisle',
        metrics: { reactions: 221, comments: 34 },
      }),
      makeFeedItem({
        id: 'msft-reddit-3',
        ticker: 'MSFT',
        source: 'reddit',
        platform: 'r/dividendlab',
        publishedAt: '2026-04-05T18:05:00Z',
        title: 'Still one of the cleanest quality names on the board',
        content:
          'The setup is not flashy, but platform breadth and steady margins make it annoyingly hard to argue with.',
        sentimentLabel: 'positive',
        sentimentScore: 0.76,
        author: 'u/steadykernel',
        metrics: { reactions: 248, comments: 29 },
      }),
      makeFeedItem({
        id: 'msft-stocktwits-1',
        ticker: 'MSFT',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-07T12:05:00Z',
        title: '$MSFT keeps acting like the low-drama adult in the room',
        content:
          'Sentiment is not euphoric, just consistently strong. That is often enough.',
        sentimentLabel: 'positive',
        sentimentScore: 0.78,
        author: '@cloudstack',
        metrics: { reactions: 255, comments: 18 },
      }),
      makeFeedItem({
        id: 'msft-stocktwits-2',
        ticker: 'MSFT',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-06T13:05:00Z',
        title: 'Mock resistance keeps easing higher',
        content:
          'This chart is not exploding, but every pushback seems shallow while the sentiment panel stays green.',
        sentimentLabel: 'positive',
        sentimentScore: 0.71,
        author: '@gridwalk',
        metrics: { reactions: 187, comments: 14 },
      }),
      makeFeedItem({
        id: 'msft-stocktwits-3',
        ticker: 'MSFT',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-05T09:15:00Z',
        title: 'Crowd expects stability more than fireworks',
        content:
          'That is not a complaint. The profile here still reads as one of the healthiest risk-adjusted setups.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.59,
        author: '@calmratio',
        metrics: { reactions: 154, comments: 12 },
      }),
    ],
  },
  AMZN: {
    symbol: 'AMZN',
    company: 'Amazon',
    sector: 'Consumer and Cloud',
    price: 188.27,
    dayChange: 0.9,
    summary:
      'Sentiment leans positive on cloud and advertising momentum, with retail efficiency serving as the stabilizer.',
    demoHeadline: 'Constructive but still balancing capex questions against broad operating leverage.',
    themes: ['AWS demand', 'ad momentum', 'retail efficiency'],
    metrics: {
      conviction: 78,
      buzzChange: 12,
      sourceDiversity: 79,
      riskNote: 'One ugly capex chart and everyone suddenly remembers shipping boxes are not free.',
    },
    sentimentSummary: {
      score: 0.64,
      delta: 0.06,
      confidence: 0.8,
      totalPosts: 337,
      positive: 187,
      neutral: 91,
      negative: 59,
    },
    sourceBreakdown: {
      news: { count: 116, sentiment: { positive: 63, neutral: 31, negative: 22 } },
      reddit: { count: 98, sentiment: { positive: 55, neutral: 28, negative: 15 } },
      stocktwits: { count: 123, sentiment: { positive: 69, neutral: 32, negative: 22 } },
    },
    timeline: [
      { timestamp: '2026-04-01T20:00:00Z', overallScore: 0.56, confidence: 0.73, totalPosts: 118, positive: 60, neutral: 33, negative: 25, price: 181.9 },
      { timestamp: '2026-04-02T20:00:00Z', overallScore: 0.58, confidence: 0.75, totalPosts: 124, positive: 64, neutral: 34, negative: 26, price: 183.2 },
      { timestamp: '2026-04-03T20:00:00Z', overallScore: 0.6, confidence: 0.76, totalPosts: 129, positive: 68, neutral: 35, negative: 26, price: 184.6 },
      { timestamp: '2026-04-04T20:00:00Z', overallScore: 0.61, confidence: 0.77, totalPosts: 135, positive: 72, neutral: 36, negative: 27, price: 185.5 },
      { timestamp: '2026-04-05T20:00:00Z', overallScore: 0.62, confidence: 0.78, totalPosts: 139, positive: 75, neutral: 38, negative: 26, price: 186.4 },
      { timestamp: '2026-04-06T20:00:00Z', overallScore: 0.63, confidence: 0.79, totalPosts: 144, positive: 79, neutral: 38, negative: 27, price: 187.5 },
      { timestamp: '2026-04-07T20:00:00Z', overallScore: 0.64, confidence: 0.8, totalPosts: 149, positive: 83, neutral: 39, negative: 27, price: 188.27 },
    ],
    feed: [
      makeFeedItem({
        id: 'amzn-news-1',
        ticker: 'AMZN',
        source: 'news',
        platform: 'Signal Ledger',
        publishedAt: '2026-04-07T11:20:00Z',
        title: 'AWS demand note offsets capex questions and a few dramatic sighs',
        content:
          'An infrastructure survey pushed the signal higher, though the write-up still flagged spending intensity as the argument nobody wants to finish.',
        sentimentLabel: 'positive',
        sentimentScore: 0.73,
        author: 'Talia Reed',
        metrics: { reactions: 169, comments: 15 },
      }),
      makeFeedItem({
        id: 'amzn-news-2',
        ticker: 'AMZN',
        source: 'news',
        platform: 'Market Mosaic',
        publishedAt: '2026-04-06T15:40:00Z',
        title: 'Ad demand stays healthy in the marketplace snapshot, to everyone’s relief',
        content:
          'The article framed advertising as the underappreciated support beam holding up a surprisingly large amount of optimism.',
        sentimentLabel: 'positive',
        sentimentScore: 0.68,
        author: 'Cole Ramirez',
        metrics: { reactions: 142, comments: 12 },
      }),
      makeFeedItem({
        id: 'amzn-news-3',
        ticker: 'AMZN',
        source: 'news',
        platform: 'Paper Street Wire',
        publishedAt: '2026-04-05T14:10:00Z',
        title: 'Shipping-cost watchlist keeps the retail story from getting too comfortable',
        content:
          'A logistics note reminded readers that margin expansion in retail still carries enough execution risk to ruin a perfectly good victory lap.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.53,
        author: 'Ivy Ross',
        metrics: { reactions: 118, comments: 10 },
      }),
      makeFeedItem({
        id: 'amzn-reddit-1',
        ticker: 'AMZN',
        source: 'reddit',
        platform: 'r/marketlab',
        publishedAt: '2026-04-07T10:05:00Z',
        title: 'AWS strength plus ads is enough for me, and yes I know that sounds lazy',
        content:
          'The setup is not flashy, but there are multiple ways for the story to work and fewer obvious weak spots than last month.',
        sentimentLabel: 'positive',
        sentimentScore: 0.75,
        author: 'u/warehousewizard',
        metrics: { reactions: 318, comments: 49 },
      }),
      makeFeedItem({
        id: 'amzn-reddit-2',
        ticker: 'AMZN',
        source: 'reddit',
        platform: 'r/cloudfloor',
        publishedAt: '2026-04-06T20:10:00Z',
        title: 'The bull case is solid, but capex still matters',
        content:
          'I like the trend, just not enough to ignore how much spending the story still requires to stay ahead.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.55,
        author: 'u/fulfillmentmath',
        metrics: { reactions: 243, comments: 38 },
      }),
      makeFeedItem({
        id: 'amzn-reddit-3',
        ticker: 'AMZN',
        source: 'reddit',
        platform: 'r/valuetech',
        publishedAt: '2026-04-05T17:30:00Z',
        title: 'Retail margins are better, but I am still watching transport costs',
        content:
          'This looks healthier than the old model, though there is still enough cost sensitivity to prevent me from getting reckless.',
        sentimentLabel: 'negative',
        sentimentScore: 0.4,
        author: 'u/parceldraft',
        metrics: { reactions: 197, comments: 27 },
      }),
      makeFeedItem({
        id: 'amzn-stocktwits-1',
        ticker: 'AMZN',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-07T12:30:00Z',
        title: '$AMZN keeps improving quietly like it does not want attention',
        content:
          'Not the loudest tape on the board, but the trend gets cleaner every session with cloud and ads doing the heavy lifting.',
        sentimentLabel: 'positive',
        sentimentScore: 0.72,
        author: '@aisledrive',
        metrics: { reactions: 241, comments: 16 },
      }),
      makeFeedItem({
        id: 'amzn-stocktwits-2',
        ticker: 'AMZN',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-06T13:20:00Z',
        title: 'Mock traders want a clearer push through 189',
        content:
          'The bias is positive, but the crowd still wants a stronger breakout before leaning too hard.',
        sentimentLabel: 'neutral',
        sentimentScore: 0.57,
        author: '@northdock',
        metrics: { reactions: 162, comments: 12 },
      }),
      makeFeedItem({
        id: 'amzn-stocktwits-3',
        ticker: 'AMZN',
        source: 'stocktwits',
        platform: 'StockTwits',
        publishedAt: '2026-04-05T09:05:00Z',
        title: 'Capex chatter keeps traders from getting too euphoric too early',
        content:
          'I still like the trend overall, but every conversation about spending intensity cools the mood fast.',
        sentimentLabel: 'negative',
        sentimentScore: 0.37,
        author: '@marginpath',
        metrics: { reactions: 149, comments: 11 },
      }),
    ],
  },
};

const SENTIMENT_LABELS = [
  { threshold: 0.66, label: 'Bullish', tone: 'positive' },
  { threshold: 0.5, label: 'Balanced', tone: 'neutral' },
  { threshold: 0, label: 'Cautious', tone: 'negative' },
];

export const TICKER_ORDER = Object.keys(TICKER_DATA);

export const getTickerData = (ticker = DEFAULT_TICKER) => {
  const normalized = String(ticker || DEFAULT_TICKER).toUpperCase();
  return TICKER_DATA[normalized] || TICKER_DATA[DEFAULT_TICKER];
};

export const getTickerList = () =>
  TICKER_ORDER.map((symbol) => {
    const item = TICKER_DATA[symbol];
    return {
      symbol,
      company: item.company,
      sector: item.sector,
      summary: item.summary,
      price: item.price,
      dayChange: item.dayChange,
      score: item.sentimentSummary.score,
      tone: SENTIMENT_LABELS.find(({ threshold }) => item.sentimentSummary.score >= threshold).tone,
    };
  });

export const getTickerTone = (score) =>
  SENTIMENT_LABELS.find(({ threshold }) => score >= threshold) || SENTIMENT_LABELS[SENTIMENT_LABELS.length - 1];

export const getAllFeedItems = () =>
  TICKER_ORDER.flatMap((symbol) => TICKER_DATA[symbol].feed).sort(
    (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
  );

export const getAllNewsItems = () => getAllFeedItems().filter((item) => item.source === 'news');

export const getDemoSummary = () => {
  const allTickers = getTickerList();
  const totalMentions = TICKER_ORDER.reduce(
    (sum, symbol) => sum + TICKER_DATA[symbol].sentimentSummary.totalPosts,
    0
  );
  const avgScore =
    allTickers.reduce((sum, item) => sum + item.score, 0) / allTickers.length;
  const strongest = [...allTickers].sort((a, b) => b.score - a.score)[0];
  const mostDebated = [...allTickers].sort(
    (a, b) => Math.abs(0.5 - a.score) - Math.abs(0.5 - b.score)
  )[0];

  return {
    lastUpdated: DEMO_LAST_UPDATED,
    notice: DEMO_NOTICE,
    totalMentions,
    avgScore,
    trackedTickers: allTickers.length,
    strongest,
    mostDebated,
  };
};
